import React from 'react';
import heroImage from '../assets/hero.webp';
import heroImage1 from '../assets/hero1.webp';

const FarmingGuidance = () => {
  return (
    <div className="mt-16"> {/* Margin top to avoid navbar overlap */}

      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-300">
            खेती की समझदारी
          </h1>
          <p className="text-xl text-white max-w-2xl mx-auto">
            आधुनिक तकनीक और पारंपरिक ज्ञान का संगम
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">

        {/* Seasonal Crop Planning */}
        <section className="mb-16 p-6 rounded-lg bg-gradient-to-br from-green-50 to-yellow-50 shadow-lg hover:shadow-xl transition-shadow duration-300">
          <h2 className="text-3xl font-bold mb-6 text-green-800 flex items-center">
            <span className="mr-2">🌾</span>
            मौसमी फसल योजना (रबी/खरीफ)
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-green-700 mb-3">रबी फसलें (अक्टूबर-मार्च)</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>गेहूं, सरसों, चना, मटर, जौ</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>कम पानी की आवश्यकता</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>ठंडे मौसम के लिए उपयुक्त</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-green-700 mb-3">खरीफ फसलें (जून-सितंबर)</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>धान, मक्का, बाजरा, मूंगफली</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>मानसून पर निर्भर</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>गर्म और नम मौसम के लिए उपयुक्त</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Soil Health Tips */}
        <section className="mb-16 p-6 rounded-lg bg-white shadow-lg hover:shadow-xl transition-shadow duration-300">
          <h2 className="text-3xl font-bold mb-6 text-green-800 flex items-center">
            <span className="mr-2">🌱</span>
            मिट्टी स्वास्थ्य सुझाव
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              "नियमित रूप से मिट्टी की जाँच करवाएं",
              "जैविक खाद का प्रयोग बढ़ाएं",
              "फसल चक्रण अपनाएं",
              "हरी खाद का उपयोग करें",
              "मिट्टी की नमी बनाए रखें",
              "अत्यधिक रासायनिक उर्वरकों से बचें"
            ].map((tip, index) => (
              <div 
                key={index}
                className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-colors duration-200"
              >
                <div className="text-green-600 font-medium">✓ {tip}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Fertilizer Advice */}
        <section className="mb-16 p-6 rounded-lg bg-gradient-to-br from-yellow-50 to-green-50 shadow-lg">
          <h2 className="text-3xl font-bold mb-6 text-green-800 flex items-center">
            <span className="mr-2">🧪</span>
            उर्वरक सलाह
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
              <thead className="bg-green-600 text-white">
                <tr>
                  <th className="py-3 px-4 text-left">फसल</th>
                  <th className="py-3 px-4 text-left">नाइट्रोजन (N)</th>
                  <th className="py-3 px-4 text-left">फॉस्फोरस (P)</th>
                  <th className="py-3 px-4 text-left">पोटाश (K)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  { crop: "गेहूं", n: "120-150 kg/ha", p: "60-80 kg/ha", k: "40-60 kg/ha" },
                  { crop: "धान", n: "100-120 kg/ha", p: "50-60 kg/ha", k: "50-60 kg/ha" },
                  { crop: "मक्का", n: "150-180 kg/ha", p: "70-80 kg/ha", k: "60-80 kg/ha" },
                  { crop: "चना", n: "20-25 kg/ha", p: "50-60 kg/ha", k: "20-30 kg/ha" },
                ].map((row, index) => (
                  <tr key={index} className="hover:bg-green-50">
                    <td className="py-3 px-4">{row.crop}</td>
                    <td className="py-3 px-4">{row.n}</td>
                    <td className="py-3 px-4">{row.p}</td>
                    <td className="py-3 px-4">{row.k}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-gray-600 italic">
            * मिट्टी परीक्षण के आधार पर सटीक मात्रा निर्धारित करें
          </p>
        </section>

        {/* Companion Planting Tips */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6 text-green-800 flex items-center">
            <span className="mr-2">👫</span>
            साथी फसलें (Companion Planting)
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md hover:scale-[1.02] transition-transform duration-300">
              <h3 className="text-xl font-semibold text-green-700 mb-3">अच्छे साथी</h3>
              <ul className="space-y-3">
                {[
                  "मक्का + सेम + कद्दू (तीन बहनें)",
                  "टमाटर + तुलसी",
                  "गोभी + प्याज/लहसुन",
                  "गाजर + मूली",
                  "धनिया + पालक"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:scale-[1.02] transition-transform duration-300">
              <h3 className="text-xl font-semibold text-red-700 mb-3">बचने योग्य संयोजन</h3>
              <ul className="space-y-3">
                {[
                  "आलू + टमाटर",
                  "प्याज + सेम",
                  "गोभी + स्ट्रॉबेरी",
                  "खीरा + आलू",
                  "मटर + प्याज/लहसुन"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-500 mr-2">✗</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Climate-wise Suggestions */}
        <section className="mb-16 p-6 rounded-lg bg-gradient-to-r from-blue-50 to-green-50 shadow-lg">
          <h2 className="text-3xl font-bold mb-6 text-green-800 flex items-center">
            <span className="mr-2">🌦️</span>
            जलवायु अनुसार सुझाव
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                climate: "शुष्क क्षेत्र",
                tips: ["बाजरा", "मूंगफली", "ग्वार", "ड्रिप सिंचाई अपनाएं"]
              },
              {
                climate: "नम क्षेत्र",
                tips: ["धान", "गन्ना", "केला", "जल निकासी का ध्यान रखें"]
              },
              {
                climate: "ठंडे क्षेत्र",
                tips: ["आलू", "गेहूं", "सरसों", "पाला सुरक्षा उपाय अपनाएं"]
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white p-5 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <h3 className="text-xl font-semibold text-blue-700 mb-3">{item.climate}</h3>
                <ul className="space-y-2">
                  {item.tips.map((tip, tipIndex) => (
                    <li key={tipIndex} className="flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        
        {/* Do's & Don'ts */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-green-800 text-center">
            करें और न करें
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
              <h3 className="text-2xl font-semibold text-green-700 mb-4 flex items-center">
                <span className="mr-2">✅</span>
                करें
              </h3>
              <ul className="space-y-3">
                {[
                  "मिट्टी परीक्षण नियमित करवाएं",
                  "जैविक खाद का प्रयोग बढ़ाएं",
                  "फसल चक्रण अपनाएं",
                  "जल संरक्षण तकनीकें अपनाएं",
                  "सही समय पर बुवाई और कटाई करें"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
              <h3 className="text-2xl font-semibold text-red-700 mb-4 flex items-center">
                <span className="mr-2">❌</span>
                न करें
              </h3>
              <ul className="space-y-3">
                {[
                  "अत्यधिक रासायनिक उर्वरकों का प्रयोग",
                  "एक ही फसल बार-बार उगाना",
                  "अनुचित सिंचाई विधियाँ",
                  "कीटनाशकों का अत्यधिक प्रयोग",
                  "बिना जानकारी के नई तकनीक अपनाना"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-600 mr-2">✗</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="mb-16 p-8 rounded-lg bg-gradient-to-r from-yellow-100 to-green-100 shadow-lg">
          <h2 className="text-3xl font-bold mb-8 text-green-800 text-center">
            सफलता की कहानियाँ
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "रमेश पटेल, मध्य प्रदेश",
                story: "जैविक खेती अपनाकर लागत 40% कम की और उत्पादन 25% बढ़ाया"
              },
              {
                name: "सीता देवी, राजस्थान",
                story: "ड्रिप सिंचाई से पानी की बचत करके सूखे में भी अच्छी फसल ली"
              },
              {
                name: "अजय कुमार, उत्तर प्रदेश",
                story: "फसल विविधीकरण से साल भर आमदनी सुनिश्चित की"
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 hover:scale-[1.02]"
              >
                <div className="text-yellow-500 text-4xl mb-3">"</div>
                <p className="text-gray-700 mb-4">{item.story}</p>
                <p className="font-semibold text-green-700">— {item.name}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section className="p-8 rounded-lg bg-white shadow-lg mt-8">
  <h2 className="text-3xl font-bold mb-8 text-green-800 text-center">
    अक्सर पूछे जाने वाले प्रश्न
  </h2>
  <div className="space-y-4">
    {[
      {
        question: "मिट्टी परीक्षण कितने समय बाद करवाना चाहिए?",
        answer: "मिट्टी परीक्षण हर 2-3 वर्षों में एक बार करवाना चाहिए। यदि आप नई फसल लगाने की योजना बना रहे हैं या मिट्टी की उर्वरता में कमी महसूस कर रहे हैं, तो परीक्षण अनिवार्य है। इससे उर्वरकों का सही उपयोग और फसल की गुणवत्ता बेहतर होती है।"
      },
      {
        question: "जैविक खेती में लाभ कितना समय लगता है?",
        answer: "शुरुआती 1-2 वर्षों में उत्पादन में गिरावट आ सकती है क्योंकि रासायनिक उर्वरकों का प्रभाव धीरे-धीरे कम होता है। लेकिन 3-4 साल के भीतर जैविक खेती मिट्टी की संरचना सुधारती है जिससे उत्पादन और लाभ दोनों में वृद्धि होती है।"
      },
      {
        question: "क्या छोटे किसान भी आधुनिक तकनीकें अपना सकते हैं?",
        answer: "हाँ, छोटे किसान भी आधुनिक तकनीकों जैसे सौर चालित पंप, ड्रिप सिंचाई, वर्मीकम्पोस्ट यूनिट्स, और मोबाइल एप्स का उपयोग कर सकते हैं। सरकार भी इन तकनीकों पर सब्सिडी देती है।"
      },
      {
        question: "फसल चक्रण क्यों जरूरी है?",
        answer: "फसल चक्रण से मिट्टी की उर्वरता बनी रहती है, कीट एवं बीमारियों का खतरा कम होता है, और जल उपयोग दक्षता भी बढ़ती है। इससे उत्पादन सतत और पर्यावरण के अनुकूल होता है।"
      },
      {
        question: "कृषि के लिए सरकारी योजनाओं की जानकारी कहाँ से लें?",
        answer: "किसान.gov.in या नजदीकी कृषि विज्ञान केंद्र (KVK) से सरकारी योजनाओं की जानकारी प्राप्त की जा सकती है। साथ ही, स्थानीय कृषि अधिकारी से मिलकर भी मार्गदर्शन लिया जा सकता है।"
      },
      {
        question: "क्या जैविक उत्पादों को बेचने के लिए विशेष प्रमाणन जरूरी है?",
        answer: "हाँ, PGS या NPOP जैसे सरकारी प्रमाणन अनिवार्य हैं यदि आप जैविक उत्पादों को बाज़ार में 'Organic' टैग से बेचना चाहते हैं। इससे उपभोक्ताओं का भरोसा भी बढ़ता है।"
      },
      {
        question: "सिंचाई के लिए सबसे किफायती और कुशल तरीका क्या है?",
        answer: "ड्रिप और स्प्रिंकलर सिंचाई प्रणाली सबसे किफायती और जल-संरक्षण के लिए उपयुक्त हैं। विशेष रूप से जल की कमी वाले क्षेत्रों में इनका उपयोग अत्यंत लाभकारी होता है।"
      }
    ].map((item, index) => (
      <details
        key={index}
        className="group border border-gray-200 rounded-lg overflow-hidden transition duration-300"
      >
        <summary className="flex justify-between items-center cursor-pointer p-4 bg-gray-50 transition-colors duration-300 hover:bg-green-100 text-gray-800 group-hover:text-green-800 group-open:text-blue-700 font-medium">
          <span>{item.question}</span>
          <svg
            className="w-5 h-5 text-green-600 transform transition-transform duration-300 group-open:rotate-180"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </summary>
        <div className="p-4 bg-white text-gray-700 leading-relaxed">
          {item.answer}
        </div>
      </details>
    ))}
  </div>
</section>


      </div>
    </div>
  );
};

export default FarmingGuidance;